import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Switch } from "antd";
import Input from "@dtci/spellbook/Input";
import Select from "@dtci/spellbook/Select";
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { useWindowsActions } from "tap/store/windows/useWindows.js";
import { InputLabel } from "tap/components/InputLabel";
import { SectionHeader } from "tap/components/SectionHeader";
import { FormCard } from "tap/components/FormCard";
import {
  DURATION_UNITS,
  WINDOW_START_DATES,
  USE_PRICING_TIER_FROM,
  STATUS_OPTIONS,
} from "../../../constants/windowConstants";
import { getSelectValue } from "../../../utils/windowHelpers";
import "./styles.scss";

/**
 * WindowSequenceForm component for create and edit modes.
 * @param {Object} props
 * @param {"create"|"edit"} props.mode - The mode of the form
 * @param {Object} props.validationErrors - Validation errors object
 */
export function WindowSequenceForm({
  mode = "create",
  validationErrors = {},
  onNextWindowIssueChange,
}) {
  const stateKey = "windowSequenceForm";

  const {
    windowSequenceForm,
    selectedWindowSequence,
    transactionalWindowsOptions,
  } = useSelector((state) => state.windows);

  const { updateWindowsForm } = useWindowsActions();

  const formData = windowSequenceForm;

  const handleInputChange = (field, value) => {
    updateWindowsForm({
      key: stateKey,
      data: { [field]: value },
    });
  };

  const getWindows = () => {
    const windows = formData?.windows || [];
    return windows.length === 0 ? [{}] : windows;
  };

  const handleWindowChange = (index, field, value) => {
    const currentWindows = formData?.windows || [];
    const newWindows = [...currentWindows];

    if (!newWindows[index]) {
      newWindows[index] = {};
    }

    newWindows[index] = {
      ...newWindows[index],
      [field]: value,
    };

    handleInputChange("windows", newWindows);
  };

  const handleAddWindow = () => {
    const currentWindows = formData?.windows || [];
    handleInputChange("windows", [
      ...currentWindows,
      {
        windowId: null,
        previousWindowPlusOneStart: true,
        windowStartDate: null,
        windowDuration: null,
        windowDurationUnit: null,
        usePricingTierFrom: null,
      },
    ]);
  };

  const handleRemoveWindow = (index) => {
    const currentWindows = formData?.windows || [];
    const newWindows = currentWindows.filter((_, i) => i !== index);
    handleInputChange("windows", newWindows);
  };

  // Disable "Add Window" when a NextWindow validation issue exists (matches old UI behavior)
  const hasNextWindowIssue = (formData?.windows || []).some((win, i, arr) => {
    if (getSelectValue(win.usePricingTierFrom) !== "NextWindow") return false;
    const nextWin = arr[i + 1];
    if (!nextWin) return true;
    const nextStartDate = getSelectValue(nextWin.windowStartDate);
    const nextPricingTier = getSelectValue(nextWin.usePricingTierFrom);
    return (
      !nextPricingTier &&
      (nextStartDate === null || nextStartDate === undefined)
    );
  });

  useEffect(() => {
    onNextWindowIssueChange?.(hasNextWindowIssue);
  }, [hasNextWindowIssue, onNextWindowIssueChange]);

  // transactionalWindowsOptions already has { value, label } from the API
  const windowOptions = transactionalWindowsOptions;

  return (
    <div className="window-sequence-form">
      <SectionHeader title="Basic Information" />
      <FormCard>
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={12}>
            <InputLabel colProps={{ span: 24 }} label="Window Sequence Name*">
              <Input
                value={formData?.windowSequenceName || ""}
                onChange={(e) =>
                  handleInputChange("windowSequenceName", e.target.value)
                }
                width="auto"
                id="windowSequenceName"
                name="windowSequenceName"
                placeholder="Enter window sequence name"
                className={
                  validationErrors.windowSequenceName ? "input-error" : ""
                }
              />
              {validationErrors.windowSequenceName && (
                <div className="validation-error">
                  {validationErrors.windowSequenceName}
                </div>
              )}
            </InputLabel>
          </Col>
          <Col span={12}>
            <InputLabel colProps={{ span: 24 }} label="Status*">
              <Select
                options={STATUS_OPTIONS}
                isSearchable={false}
                isClearable={false}
                onChange={(value) => handleInputChange("status", value)}
                value={STATUS_OPTIONS.find(
                  (opt) => opt.value === getSelectValue(formData?.status),
                )}
                placeholder="Select status"
              />
            </InputLabel>
          </Col>
        </Row>
      </FormCard>

      <SectionHeader title="Windows" />
      {validationErrors.windows && (
        <div
          className="validation-error"
          style={{ marginBottom: 12, paddingLeft: 16 }}
        >
          {validationErrors.windows}
        </div>
      )}

      {getWindows().map((window, index) => (
        <FormCard key={index} style={{ marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <Typography variant="T30" bold>
              Window {index + 1}
            </Typography>
            <Button
              iconType="fa-solid fa-trash"
              styleType="link"
              onClick={() => handleRemoveWindow(index)}
              disabled={index === 0}
              style={{
                color: index === 0 ? "#9e9e9e" : "#e53935",
                cursor: index === 0 ? "not-allowed" : "pointer",
              }}
            >
              Remove
            </Button>
          </div>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <InputLabel colProps={{ span: 24 }} label="Name*">
                <Select
                  options={windowOptions}
                  isSearchable
                  isClearable
                  onChange={(value) =>
                    handleWindowChange(index, "windowId", value)
                  }
                  value={windowOptions.find(
                    (opt) => opt.value === getSelectValue(window.windowId),
                  )}
                  placeholder="Select window"
                />
                {validationErrors[`windows_${index}_windowId`] && (
                  <div className="validation-error">
                    {validationErrors[`windows_${index}_windowId`]}
                  </div>
                )}
              </InputLabel>
            </Col>
          </Row>

          {index > 0 && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <InputLabel
                  colProps={{ span: 24 }}
                  label="Previous Window +1 Start"
                >
                  <Switch
                    checked={window.previousWindowPlusOneStart ?? true}
                    onChange={(checked) => {
                      const currentWindows = formData?.windows || [];
                      const newWindows = [...currentWindows];

                      if (!newWindows[index]) {
                        newWindows[index] = {};
                      }

                      newWindows[index] = {
                        ...newWindows[index],
                        previousWindowPlusOneStart: checked,
                      };

                      // Clear usePricingTierFrom when toggling previousWindowPlusOne off
                      if (!checked) {
                        newWindows[index].usePricingTierFrom = null;
                      }

                      handleInputChange("windows", newWindows);
                    }}
                  />
                </InputLabel>
              </Col>
            </Row>
          )}

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <InputLabel
                colProps={{ span: 24 }}
                label={index === 0 ? "Window Start Date*" : "Window Start Date"}
              >
                <Select
                  options={WINDOW_START_DATES}
                  isSearchable
                  isClearable
                  onChange={(value) => {
                    const currentWindows = formData?.windows || [];
                    const newWindows = [...currentWindows];

                    if (!newWindows[index]) {
                      newWindows[index] = {};
                    }

                    newWindows[index] = {
                      ...newWindows[index],
                      windowStartDate: value,
                    };

                    // Clear usePricingTierFrom if windowStartDate is selected (check for null, not falsy)
                    if (value !== null && index > 0) {
                      newWindows[index].usePricingTierFrom = null;
                    }

                    handleInputChange("windows", newWindows);
                  }}
                  value={WINDOW_START_DATES.find(
                    (opt) =>
                      opt.value === getSelectValue(window.windowStartDate),
                  )}
                  placeholder="Select window start date"
                  isDisabled={false}
                />
                {validationErrors[`windows_${index}_windowStartDate`] && (
                  <div className="validation-error">
                    {validationErrors[`windows_${index}_windowStartDate`]}
                  </div>
                )}
              </InputLabel>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <InputLabel colProps={{ span: 24 }} label="Duration*">
                <Input
                  type="number"
                  min="0"
                  max="999"
                  value={window.windowDuration || ""}
                  onChange={(e) =>
                    handleWindowChange(index, "windowDuration", e.target.value)
                  }
                  width="auto"
                  placeholder="Enter duration"
                  className={
                    validationErrors[`windows_${index}_windowDuration`]
                      ? "input-error"
                      : ""
                  }
                />
                {validationErrors[`windows_${index}_windowDuration`] && (
                  <div className="validation-error">
                    {validationErrors[`windows_${index}_windowDuration`]}
                  </div>
                )}
              </InputLabel>
            </Col>
            <Col span={12}>
              <InputLabel colProps={{ span: 24 }} label="Duration Unit*">
                <Select
                  options={DURATION_UNITS}
                  isSearchable={false}
                  isClearable
                  onChange={(value) =>
                    handleWindowChange(index, "windowDurationUnit", value)
                  }
                  value={DURATION_UNITS.find(
                    (opt) =>
                      opt.value === getSelectValue(window.windowDurationUnit),
                  )}
                  placeholder="Select unit"
                />
                {validationErrors[`windows_${index}_windowDurationUnit`] && (
                  <div className="validation-error">
                    {validationErrors[`windows_${index}_windowDurationUnit`]}
                  </div>
                )}
              </InputLabel>
            </Col>
          </Row>

          {index > 0 && (
            <Row gutter={16}>
              <Col span={24}>
                <InputLabel
                  colProps={{ span: 24 }}
                  label="Use Pricing Tier From"
                >
                  <Select
                    options={USE_PRICING_TIER_FROM}
                    isSearchable
                    isClearable
                    onChange={(value) =>
                      handleWindowChange(index, "usePricingTierFrom", value)
                    }
                    value={USE_PRICING_TIER_FROM.find(
                      (opt) =>
                        opt.value === getSelectValue(window.usePricingTierFrom),
                    )}
                    placeholder="Select pricing tier"
                    isDisabled={
                      // Disabled when previousWindowPlusOneStart is off or windowStartDate is set
                      !(window.previousWindowPlusOneStart ?? true) ||
                      (getSelectValue(window.windowStartDate) !== null &&
                        getSelectValue(window.windowStartDate) !== undefined)
                    }
                  />
                  {validationErrors[`windows_${index}_usePricingTierFrom`] && (
                    <div className="validation-error">
                      {validationErrors[`windows_${index}_usePricingTierFrom`]}
                    </div>
                  )}
                </InputLabel>
              </Col>
            </Row>
          )}
        </FormCard>
      ))}

      <div style={{ padding: "0 16px", marginBottom: 20 }}>
        <Button
          iconType="fa-solid fa-plus"
          styleType="link"
          onClick={handleAddWindow}
          disabled={hasNextWindowIssue}
          style={{ color: hasNextWindowIssue ? "#9e9e9e" : "#3611c8" }}
        >
          Add Window
        </Button>
      </div>
    </div>
  );
}
